import { useRef } from "react";
import Slider from "react-slick";
import {
  Box,
  Container,
  IconButton,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { Icon } from "@iconify/react";

import { useResponsive } from "@app/hooks";
import { Strings } from "@app/constants";
import { testimonial } from "./testimonialData";
import { TestimonialCard } from "./testimonialCard";

const Testimonial = () => {
  const theme = useTheme();
  const data = testimonial;
  const slideRef = useRef<Slider>(null);
  const isMobile = useResponsive("down", "sm");

  const settings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: true,
    variableWidth: false,
    adaptiveHeight: true,
    customPaging: () => (
      <Box
        className="custom-dot"
        sx={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: alpha(theme.palette.grey[500], 0.35),
          transition: "all 0.3s ease",
          cursor: "pointer",
        }}
      />
    ),
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 5, md: 8 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Stack
          alignItems="center"
          spacing={1.5}
          sx={{ mb: { xs: 3.5, md: 5 }, textAlign: "center" }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontFamily: "Work Sans, sans-serif",
              fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
              color: theme.palette.grey[900],
            }}
          >
            {Strings.testimonial.whatOurClientSay}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[600],
              maxWidth: 560,
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          ></Typography>
        </Stack>

        {/* Testimonial Slider Container */}
        <Box
          sx={{
            position: "relative",
            maxWidth: 880,
            mx: "auto",
            px: { xs: 0, sm: 4 },
            "& .slick-slider": {
              pb: 6,
              overflow: "visible",
            },
            "& .slick-track": {
              display: "flex",
              alignItems: "stretch",
            },
            "& .slick-slide": {
              height: "auto",
              "& > div": {
                height: "100%",
              },
            },
            "& .slick-dots": {
              bottom: 0,
              position: "absolute",
              display: "flex !important",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
              listStyle: "none",
              p: 0,
              m: 0,
              "& li": {
                width: "auto !important",
                height: "auto !important",
                margin: 0,
                display: "inline-flex !important",
                alignItems: "center",
                justifyContent: "center",
              },
              "& li button": {
                padding: 0,
                width: "auto",
                height: "auto",
                border: "none",
                background: "none",
                cursor: "pointer",
                "&:before": {
                  display: "none",
                },
              },
              "& li .custom-dot": {
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: alpha(theme.palette.grey[500], 0.3),
                transition: "all 0.3s ease",
              },
              "& li.slick-active .custom-dot": {
                width: 24,
                backgroundColor: theme.palette.primary.main,
              },
            },
          }}
        >
          <Slider ref={slideRef} {...settings}>
            {data?.map((item, index) => (
              <Box key={index} sx={{ px: { xs: 0.5, sm: 1 } }}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.grey[200],
                    borderRadius: { xs: 3, md: 4 },
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.28)}`,
                    position: "relative",
                    px: { xs: 2, sm: 4, md: 5 },
                    py: { xs: 3, sm: 4, md: 5 },
                  }}
                >
                  <TestimonialCard
                    name={item.name}
                    position={item.position}
                    rating={item.rating}
                    testimonial={item.testimonial}
                  />
                </Box>
              </Box>
            ))}
          </Slider>

          {/* Previous / Next Arrow Controls for Tablet & Desktop */}
          {!isMobile && (
            <>
              <IconButton
                onClick={() => slideRef?.current?.slickPrev()}
                aria-label="Previous testimonial"
                sx={{
                  position: "absolute",
                  left: { sm: -18, md: -22 },
                  top: "calc(50% - 24px)",
                  transform: "translateY(-50%)",
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.grey[800],
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                  width: 44,
                  height: 44,
                  zIndex: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    transform: "translateY(-50%) scale(1.06)",
                  },
                }}
              >
                <Icon
                  icon="solar:alt-arrow-left-linear"
                  width={22}
                  height={22}
                />
              </IconButton>

              <IconButton
                onClick={() => slideRef?.current?.slickNext()}
                aria-label="Next testimonial"
                sx={{
                  position: "absolute",
                  right: { sm: -18, md: -22 },
                  top: "calc(50% - 24px)",
                  transform: "translateY(-50%)",
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.grey[800],
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                  width: 44,
                  height: 44,
                  zIndex: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                    transform: "translateY(-50%) scale(1.06)",
                  },
                }}
              >
                <Icon
                  icon="solar:alt-arrow-right-linear"
                  width={22}
                  height={22}
                />
              </IconButton>
            </>
          )}

          {/* Mobile Arrows Navigation */}
          {isMobile && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={2}
              sx={{ mt: 3 }}
            >
              <IconButton
                onClick={() => slideRef?.current?.slickPrev()}
                aria-label="Previous testimonial"
                sx={{
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.grey[800],
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                  },
                }}
              >
                <Icon
                  icon="solar:alt-arrow-left-linear"
                  width={20}
                  height={20}
                />
              </IconButton>
              <IconButton
                onClick={() => slideRef?.current?.slickNext()}
                aria-label="Next testimonial"
                sx={{
                  bgcolor: theme.palette.common.white,
                  color: theme.palette.grey[800],
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.35)}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  width: 40,
                  height: 40,
                  "&:hover": {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.common.white,
                  },
                }}
              >
                <Icon
                  icon="solar:alt-arrow-right-linear"
                  width={20}
                  height={20}
                />
              </IconButton>
            </Stack>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export { Testimonial };
